import { request } from '@/shared/utils/requestBuilder';
import { lerDadosDaRespostaDoCore } from '@/shared/utils/respostaDoCore';

import type {
  AssinaturasConsolidadasResponse,
  ConsultaAssinaturas,
  CorpoDaExportacao,
  ErroDaConsulta,
  OpcoesDeFiltro,
  PedidoDeParticipacoes,
  PaginaDeParticipacoes,
  StatusDaExportacaoDeAssinaturas,
} from '../types/negociacaoAssinaturasTypes';
import {
  queryDaConsulta,
  queryDasOpcoesDeFiltro,
  PARTICIPACOES_POR_PAGINA,
} from '../utils/consultaAssinaturas';

/**
 * Falha tipada: o thunk passa adiante o `status` para a tela separar a recusa de permissão (403)
 * das demais falhas sem comparar texto.
 */
export class FalhaDasAssinaturas extends Error {
  readonly erro: ErroDaConsulta;

  constructor(erro: ErroDaConsulta) {
    super(erro.mensagem);
    this.name = 'FalhaDasAssinaturas';
    this.erro = erro;
  }
}

/**
 * O 403 destas rotas é a recusa de permissão do escritório atual (VIEWER na consulta e nas opções,
 * a permissão única no link e no export). A recuperação de workspace do `requestBuilder` refaria a
 * chamada sem `X-Workspace-Id` e recarregaria a página, trocando a mensagem de "sem acesso" por um
 * reload — por isso todas saem com ela desligada, como as rotas de chaves de API.
 */
const chamar = (endpoint: string, method: 'GET' | 'POST', body?: object, signal?: AbortSignal) =>
  request({ endpoint, method, body, signal, recuperarWorkspaceNo403: false });

/** A consulta responde com envelope `{ success, data }`; o status do export, sem envelope. */
const lerResposta = <T>(response: Response, mensagemPadrao: string): Promise<T> =>
  lerDadosDaRespostaDoCore<T>(
    response,
    mensagemPadrao,
    ({ status, mensagem }) => new FalhaDasAssinaturas({ status, mensagem }),
  );

export const negociacaoAssinaturasService = {
  async consultar(consulta: ConsultaAssinaturas): Promise<AssinaturasConsolidadasResponse> {
    const query = queryDaConsulta(consulta);
    const response = await chamar(`/negociacao/assinaturas?${query}`, 'GET');
    return lerResposta<AssinaturasConsolidadasResponse>(
      response,
      'Não foi possível carregar as assinaturas dos acordos',
    );
  },

  async consultarOpcoesDeFiltro(busca: string, page = 1): Promise<OpcoesDeFiltro> {
    const query = queryDasOpcoesDeFiltro(busca, page);
    const response = await chamar(`/negociacao/assinaturas/opcoes?${query}`, 'GET');
    return lerResposta<OpcoesDeFiltro>(response, 'Não foi possível carregar as opções de filtro');
  },

  async consultarParticipacoes({
    acordoId,
    consulta,
    page,
  }: PedidoDeParticipacoes): Promise<PaginaDeParticipacoes> {
    const query = queryDaConsulta({ ...consulta, page, pageSize: PARTICIPACOES_POR_PAGINA });
    const response = await chamar(
      `/negociacao/assinaturas/acordos/${encodeURIComponent(acordoId)}/participacoes?${query}`,
      'GET',
    );
    return lerResposta<PaginaDeParticipacoes>(response, 'Não foi possível carregar mais pessoas');
  },

  /** O endereço é credencial de assinatura: quem chama o usa e descarta, sem guardar em estado. */
  async buscarEnderecoDeAssinatura(signatarioId: string): Promise<string> {
    const response = await chamar(
      `/negociacao/assinaturas/signatarios/${encodeURIComponent(signatarioId)}/link`,
      'GET',
    );
    const { link } = await lerResposta<{ link: string }>(
      response,
      'Não foi possível obter o endereço de assinatura',
    );
    return link;
  },

  async iniciarExportacao(corpo: CorpoDaExportacao): Promise<{ exportId: string }> {
    const response = await chamar('/negociacao/assinaturas/export', 'POST', corpo);
    return lerResposta<{ exportId: string }>(response, 'Erro ao iniciar a exportação');
  },

  /**
   * O status é o mesmo endpoint do export de negociações, que responde sem envelope. O `signal`
   * cancela a consulta quando o prazo do export acaba (`aguardarExportacaoPronta`).
   */
  async consultarExportacao(
    exportId: string,
    signal?: AbortSignal,
  ): Promise<StatusDaExportacaoDeAssinaturas> {
    const response = await chamar(
      `/negociacao/casos/export/${encodeURIComponent(exportId)}`,
      'GET',
      undefined,
      signal,
    );
    return lerResposta<StatusDaExportacaoDeAssinaturas>(
      response,
      'Erro ao consultar status da exportação',
    );
  },
};
